import type { LoomInitiativeDTO, LoomInitiativeStatus } from '../auth/apiClient.js';

/**
 * TRELLIS status helpers — shared by the dashboard, status lanes, detail panel, and the editor
 * marker. Keeps the status→label→color mapping in one place so chips read identically everywhere.
 */

/** The four lanes the reworked grid uses, in display order (archived is hidden from lanes). */
export const STATUS_LANES: LoomInitiativeStatus[] = ['active', 'stale', 'blocked', 'done'];

/** Human label for a status. */
export function statusLabel(status: LoomInitiativeStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'stale':
      return 'Stale';
    case 'blocked':
      return 'Blocked';
    case 'done':
      return 'Done';
    case 'archived':
      return 'Archived';
    default:
      return status;
  }
}

/**
 * The effective status of an initiative: the server field when present, else a sensible default
 * derived from `archived` (legacy boards carry no `status`). Never returns undefined so callers can
 * key maps/switches safely.
 */
export function effectiveStatus(i: LoomInitiativeDTO): LoomInitiativeStatus {
  if (i.status) return i.status;
  return i.archived ? 'archived' : 'active';
}

const DAY_MS = 86_400_000;

/** Whole days between `at` and now (floored, never negative). */
export function daysAgo(at: number | undefined | null, now = Date.now()): number | null {
  if (!at) return null;
  return Math.max(0, Math.floor((now - at) / DAY_MS));
}

/** "last activity N days ago" / "today" / "yesterday" — null `at` → "no activity yet". */
export function lastActivityLabel(at: number | undefined | null, now = Date.now()): string {
  const d = daysAgo(at, now);
  if (d == null) return 'no activity yet';
  if (d === 0) return 'active today';
  if (d === 1) return 'active yesterday';
  return `last activity ${d} days ago`;
}

/** Due-date relative label ("due in 3 days", "overdue by 2 days", "due today"), or null if no date. */
export function dueLabel(due: number | null | undefined, now = Date.now()): string | null {
  if (due == null) return null;
  const d = Math.round((due - now) / DAY_MS);
  if (d === 0) return 'due today';
  if (d > 0) return `due in ${d} day${d === 1 ? '' : 's'}`;
  return `overdue by ${-d} day${d === -1 ? '' : 's'}`;
}

/** Format an epoch-ms timestamp as a short local date (e.g. "Oct 27"). */
export function shortDate(at: number): string {
  try {
    return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
